import axios from 'axios';

/** URL base da API: em produção (HTTPS ou porta 80/443) usa mesmo origin; senão usa REACT_APP_API_URL ou host:5000. */
export const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL !== undefined && process.env.REACT_APP_API_URL !== '') return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    if (protocol === 'https:' || !port || port === '80' || port === '443') return ''; // produção: mesmo origin
    return `${protocol}//${hostname}:5000`;
  }
  return 'http://localhost:5000';
};

const api = axios.create({
  baseURL: getApiBaseUrl(),
});

/** URL base para links de download (documentos, PDFs). Quando baseURL é vazio (mesmo origin), usa window.location.origin. */
export const getDownloadBaseUrl = () => {
  const b = api.defaults.baseURL;
  if (b && String(b).trim() !== '') return b.replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location && window.location.origin) return window.location.origin;
  return process.env.REACT_APP_API_URL || 'http://localhost:5000';
};

/** Abre o PDF da licença via API (com token), evitando Cannot GET /uploads/... em servidor com proxy. */
export function openLicencaPdf(arquivoPdfPath) {
  if (!arquivoPdfPath) return;
  const filename = arquivoPdfPath.replace(/^\/uploads\//, '') || arquivoPdfPath.split('/').pop();
  const url = `${getApiBaseUrl()}/api/licencas/arquivo/${encodeURIComponent(filename)}`;
  const token = localStorage.getItem('token');
  fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
    .then((r) => {
      if (!r.ok) {
        return r.json().catch(() => ({})).then((body) => {
          const msg = body?.error || r.statusText || 'Erro ao baixar PDF da licença';
          throw new Error(msg);
        });
      }
      return r.blob();
    })
    .then((blob) => {
      const u = URL.createObjectURL(blob);
      window.open(u, '_blank', 'noopener');
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error('Erro ao abrir PDF da licença:', err);
      const msg = err?.message || 'Não foi possível abrir o PDF da licença. O arquivo pode não existir mais no servidor.';
      window.alert(msg);
    });
}

// Interceptor para adicionar token em todas as requisições
api.interceptors.request.use(
  (config) => {
    // Marca início da requisição para cálculo de duração no log
    config.metadata = { startTime: new Date() };

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // FormData: não definir Content-Type para que axios adicione o boundary automaticamente
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Log detalhado da requisição no console do front
    try {
      const { method, url, baseURL, params, data } = config;
      // Evitar logar payloads gigantes de arquivo
      const safeData =
        data instanceof FormData
          ? '[FormData]'
          : data;
      // Não logar header Authorization
      const { Authorization, authorization, ...safeHeaders } = config.headers || {};
      // eslint-disable-next-line no-console
      console.info('[API REQUEST]', {
        method: method?.toUpperCase(),
        url,
        baseURL,
        params,
        data: safeData,
        headers: safeHeaders,
      });
    } catch {
      // se der erro no log, não quebra a requisição
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para logar todas as respostas (sucesso e erro) no console do front
api.interceptors.response.use(
  (response) => {
    try {
      const endTime = new Date();
      const startTime = response.config?.metadata?.startTime;
      const durationMs = startTime ? endTime - startTime : null;

      const { status, config, data } = response;
      const { method, url, baseURL } = config;

      // eslint-disable-next-line no-console
      console.info('[API RESPONSE]', {
        method: method?.toUpperCase(),
        url,
        baseURL,
        status,
        durationMs,
        data,
      });
    } catch {
      // ignora erros de log
    }
    return response;
  },
  (error) => {
    try {
      const endTime = new Date();
      const startTime = error.config?.metadata?.startTime;
      const durationMs = startTime ? endTime - startTime : null;

      const status = error.response?.status || null;
      const data = error.response?.data || null;
      const { method, url, baseURL } = error.config || {};

      // eslint-disable-next-line no-console
      console.error('[API ERROR]', {
        method: method?.toUpperCase(),
        url,
        baseURL,
        status,
        durationMs,
        data,
        message: error.message,
      });
    } catch {
      // se der erro ao logar, só segue com o erro original
    }
    return Promise.reject(error);
  }
);

export default api;
